import { useCallback, useEffect, useRef, useState } from "react";
import errorFunctions from "@store/errorFunctions";

/*
`protectedFetch` turns the GET `data` argument into query params with
`new URLSearchParams(data)`, which stringifies null/undefined into the literal
strings "null"/"undefined". Strip empty keys before they get there.
*/
export const cleanParams = function (params) {
  if (!params) return null;
  const out = {};
  Object.keys(params).forEach(function (key) {
    const value = params[key];
    if (value === null || value === undefined || value === "") return;
    out[key] = value;
  });
  return Object.keys(out).length ? out : null;
};

/*
The loading/error/data triple every card on this page needs, without the
per-request copy-paste the older dashboard tabs carry.

Stale data is kept visible while a refetch is in flight, so switching the period
pill doesn't collapse the whole page back to skeletons - only the first load
shows them (`hasLoadedOnce`).
*/
export default function useApiResource(protectedFetch, url, params, options) {
  const skip = !!(options && options.skip);
  const [state, setState] = useState({
    loading: !skip,
    error: null,
    data: null,
    hasLoadedOnce: false,
  });

  // Params objects are rebuilt on every render; serialise for a stable dep.
  const paramsKey = JSON.stringify(cleanParams(params) || {});
  const ignoreRef = useRef(false);

  const load = useCallback(
    function () {
      if (skip || !protectedFetch || !url) return;

      setState(function (prev) {
        return {
          loading: true,
          error: null,
          data: prev.data,
          hasLoadedOnce: prev.hasLoadedOnce,
        };
      });

      protectedFetch(
        url,
        "GET",
        cleanParams(JSON.parse(paramsKey)),
        errorFunctions.checkResponseThenJson
      )
        .then(function (res) {
          if (ignoreRef.current) return;
          setState({
            loading: false,
            error: null,
            data: res,
            hasLoadedOnce: true,
          });
        })
        .catch(function (err) {
          if (ignoreRef.current) return;
          setState({
            loading: false,
            error: (err && err.message) || "Failed to load data.",
            data: null,
            hasLoadedOnce: true,
          });
        });
    },
    [protectedFetch, url, paramsKey, skip]
  );

  useEffect(
    function () {
      ignoreRef.current = false;
      load();
      // A fast period toggle must not let an earlier response land last.
      return function () {
        ignoreRef.current = true;
      };
    },
    [load]
  );

  // For writes whose response is the full resource (e.g. PATCH /ticket/{id}/),
  // so the page can render it without a second GET.
  const setData = useCallback(function (data) {
    setState(function (prev) {
      return {
        loading: prev.loading,
        error: null,
        data: data,
        hasLoadedOnce: true,
      };
    });
  }, []);

  return {
    loading: state.loading,
    error: state.error,
    data: state.data,
    hasLoadedOnce: state.hasLoadedOnce,
    reload: load,
    setData: setData,
  };
}
